import React, { useEffect, useState } from 'react';
import {
  fetchSensorCalibration,
  fetchSensorCalibrationZip,
} from '../../services/api';
import { SensorCalibration } from '../../types/types';

export default function CalibrationSection() {
  const [sensorCalibration, setSensorCalibration] = useState<
    SensorCalibration[]
  >([]);
  const [selectedSensorCalibrationKey, setSelectedSensorCalibrationKey] =
    useState<string>('');
  const [loadingSensorCalibration, setLoadingSensorCalibration] =
    useState(true);
  const [zipError, setZipError] = useState<string | null>(null);
  const [zipWarning, setZipWarning] = useState<string | null>(null);

  useEffect(() => {
    fetchSensorCalibration()
      .then((data) => {
        setSensorCalibration(data);
        setSelectedSensorCalibrationKey(data[0]?.key || '');
      })
      .catch((err) => console.error('❌ Error fetching calibration:', err))
      .finally(() => setLoadingSensorCalibration(false));
  }, []);

  const selectedSensorCalibration = sensorCalibration.find(
    (s) => s.key === selectedSensorCalibrationKey
  );

  return (
    <section className="mb-20 px-4">
      <h2
        id="calibration"
        className="scroll-mt-24 text-3xl md:text-4xl text-center font-extrabold mb-8 tracking-tight"
      >
        <a
          href="#calibration"
          className="text-gray-800 dark:text-gray-100 hover:text-blue-500 transition-colors"
        >
          Calibrations
        </a>
      </h2>

      {loadingSensorCalibration ? (
        <div className="text-center py-10 animate-pulse text-gray-500">
          Loading sensor calibrations...
        </div>
      ) : sensorCalibration.length === 0 ? (
        <div className="text-center py-10 text-red-500 text-lg font-medium">
          ⚠️ No sensor calibrations found! Check console for details.
        </div>
      ) : (
        <>
          <div className="flex md:hidden justify-center mb-6">
            <select
              value={selectedSensorCalibrationKey}
              onChange={(e) => setSelectedSensorCalibrationKey(e.target.value)}
              className="w-full max-w-xs px-4 py-2 rounded-xl border border-gray-300 shadow-sm
                text-gray-700 dark:text-gray-200 dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 transition"
            >
              {sensorCalibration.map((calibration) => (
                <option key={calibration.key} value={calibration.key}>
                  {calibration.name}
                </option>
              ))}
            </select>
          </div>

          <div className="hidden md:flex flex-wrap justify-center gap-2 bg-gray-100 dark:bg-gray-800/70 rounded-2xl p-2 max-w-4xl mx-auto mb-8 shadow-inner">
            {sensorCalibration.map((calibration) => (
              <button
                key={calibration.key}
                onClick={() => setSelectedSensorCalibrationKey(calibration.key)}
                className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                  selectedSensorCalibrationKey === calibration.key
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-gray-700/60'
                }`}
              >
                {calibration.name}
              </button>
            ))}
          </div>
        </>
      )}

      {selectedSensorCalibration && (
        <div
          className="rounded-2xl bg-white/60 dark:bg-gray-800/70
          p-6 md:p-8 max-w-4xl mx-auto shadow-lg transition-all duration-500 hover:shadow-xl"
        >
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="flex-1 space-y-3">
              <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed italic">
                {selectedSensorCalibration.description}
              </p>
              {selectedSensorCalibration.link && (
                <a
                  href={selectedSensorCalibration.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-blue-500 font-semibold hover:underline"
                >
                  {selectedSensorCalibration.linkLabel}
                </a>
              )}
              <button
                onClick={async () => {
                  try {
                    setZipError(null);
                    setZipWarning(
                      'Downloading started. It may take some time before it appears in your downloads...'
                    );

                    await fetchSensorCalibrationZip(
                      selectedSensorCalibration.dataLink
                    );

                    setTimeout(() => setZipWarning(null), 5000);
                  } catch (err: any) {
                    setZipWarning(null);
                    setZipError(
                      err.message || 'An error occurred while fetching ZIP'
                    );
                  }
                }}
                className="
    mt-3 px-5 py-2.5 rounded-xl font-semibold
    bg-blue-600 text-white
    hover:bg-blue-700 active:bg-blue-800
    dark:bg-blue-500 dark:hover:bg-blue-600
    transition-all shadow-md hover:shadow-lg
    flex items-center gap-2
  "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M7.5 12l4.5 4.5m0 0l4.5-4.5m-4.5 4.5V3"
                  />
                </svg>
                Download calibration data
              </button>

              {zipWarning && (
                <p className="mt-3 text-yellow-600 font-medium">
                  ⚠️ {zipWarning}
                </p>
              )}

              {zipError && (
                <p className="mt-3 text-red-500 font-medium">⚠️ {zipError}</p>
              )}
            </div>

            {selectedSensorCalibration.image && (
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <img
                  src={selectedSensorCalibration.image}
                  alt={selectedSensorCalibration.name}
                  className="w-full max-w-[250px] rounded-xl shadow-md border border-gray-300 dark:border-gray-700 object-cover"
                />
              </div>
            )}
          </div>

          {(() => {
            const details = selectedSensorCalibration.details ?? [];
            return (
              details.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-center text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                    Details
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-900/60 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                    <p className="text-gray-800 dark:text-gray-200 text-justify leading-relaxed text-[15px]">
                      {details.map((detail, index) => (
                        <span key={index}>
                          {detail}
                          {index < details.length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              )
            );
          })()}
        </div>
      )}
    </section>
  );
}
